import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import {
  createUpsellSuggestion,
  listUpsellSuggestions,
  patchUpsellSuggestion,
} from '@/lib/api/customer-status-upsell.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

type SuggestionRow = {
  id: string;
  name?: string;
  title?: string;
  message?: string;
  active?: boolean;
  isSystem?: boolean;
  priority?: number;
  showAt?: string;
  actionLabel?: string | null;
  actionType?: string | null;
};

type FormState = {
  name: string;
  title: string;
  message: string;
  actionLabel: string;
  actionType: string;
  showAt: string;
  priority: string;
  active: boolean;
};

const EMPTY_FORM: FormState = {
  name: '',
  title: '',
  message: '',
  actionLabel: 'Add service',
  actionType: 'add_service',
  showAt: 'ticket_create',
  priority: '100',
  active: true,
};

export function UpsellSettingsPage() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const listQuery = useQuery({
    queryKey: ['upsell-suggestions'],
    queryFn: listUpsellSuggestions,
  });

  const rows = useMemo(
    () => (Array.isArray(listQuery.data) ? (listQuery.data as SuggestionRow[]) : []),
    [listQuery.data],
  );

  const createMutation = useMutation({
    mutationFn: () =>
      createUpsellSuggestion({
        name: form.name.trim(),
        title: form.title.trim(),
        message: form.message.trim(),
        actionLabel: form.actionLabel.trim() || null,
        actionType: form.actionType || null,
        showAt: form.showAt,
        priority: Number(form.priority) || 100,
        active: form.active,
        triggerType: 'custom_rule',
        triggerConfig: {},
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['upsell-suggestions'] });
      setCreating(false);
      setForm(EMPTY_FORM);
      toastSuccess('Upsell suggestion created');
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      patchUpsellSuggestion(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['upsell-suggestions'] });
      setEditingId(null);
      toastSuccess('Upsell suggestion updated');
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  function startEdit(row: SuggestionRow) {
    setCreating(false);
    setEditingId(row.id);
    setForm({
      name: String(row.name ?? ''),
      title: String(row.title ?? ''),
      message: String(row.message ?? ''),
      actionLabel: String(row.actionLabel ?? 'Add service'),
      actionType: String(row.actionType ?? 'add_service'),
      showAt: String(row.showAt ?? 'ticket_create'),
      priority: String(row.priority ?? 100),
      active: row.active !== false,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-950">Upsell Suggestions</h2>
          <p className="mt-1 text-sm text-slate-500">
            Prompt staff with relevant add-ons during ticket create and checkout.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/reports/upsell"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            View report
          </Link>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
            onClick={() => {
              setEditingId(null);
              setCreating(true);
              setForm(EMPTY_FORM);
            }}
          >
            <Plus className="h-4 w-4" />
            Custom suggestion
          </button>
        </div>
      </div>

      {creating || editingId ? (
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-slate-950">
            {creating ? 'Create suggestion' : 'Edit suggestion'}
          </h3>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Internal name</span>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Priority</span>
              <input
                type="number"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              />
            </label>
            <label className="block text-sm md:col-span-2">
              <span className="font-medium text-slate-700">Title</span>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </label>
            <label className="block text-sm md:col-span-2">
              <span className="font-medium text-slate-700">Message</span>
              <textarea
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                rows={3}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Action label</span>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.actionLabel}
                onChange={(e) => setForm((f) => ({ ...f, actionLabel: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Show at</span>
              <select
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={form.showAt}
                onChange={(e) => setForm((f) => ({ ...f, showAt: e.target.value }))}
              >
                <option value="ticket_create">Ticket create</option>
                <option value="checkout">Checkout</option>
              </select>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
            />
            Active
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
              onClick={() => {
                setCreating(false);
                setEditingId(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              disabled={createMutation.isPending || patchMutation.isPending}
              onClick={() => {
                if (creating) {
                  createMutation.mutate();
                  return;
                }
                if (!editingId) return;
                patchMutation.mutate({
                  id: editingId,
                  body: {
                    name: form.name.trim(),
                    title: form.title.trim(),
                    message: form.message.trim(),
                    actionLabel: form.actionLabel.trim() || null,
                    showAt: form.showAt,
                    priority: Number(form.priority) || 100,
                    active: form.active,
                  },
                });
              }}
            >
              Save
            </button>
          </div>
        </section>
      ) : null}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Show at</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="px-4 py-3 font-semibold text-slate-950">
                  {row.name ?? 'Suggestion'}
                  {row.isSystem ? (
                    <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">
                      System
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-slate-600">{row.title ?? '—'}</td>
                <td className="px-4 py-3 capitalize text-slate-600">
                  {String(row.showAt ?? '—').replace(/_/g, ' ')}
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                      row.active !== false
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-slate-100 text-slate-500'
                    }`}
                    onClick={() =>
                      patchMutation.mutate({
                        id: row.id,
                        body: { active: row.active === false },
                      })
                    }
                  >
                    {row.active !== false ? 'On' : 'Off'}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-primary"
                    onClick={() => startEdit(row)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                </td>
              </tr>
            ))}
            {listQuery.isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  Loading suggestions…
                </td>
              </tr>
            ) : null}
            {!listQuery.isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No upsell suggestions yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </div>
  );
}
